import React from 'react';
import { APP_CONFIG } from '../config/constants';

export const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Politique de confidentialité</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 text-sm mb-8">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-CH')} | Conforme LPD (Suisse) et RGPD (UE)
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Responsable du traitement</h2>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>{APP_CONFIG.name}</strong><br />
                  Application réalisée en partenariat avec la<br />
                  <a 
                    href={APP_CONFIG.partners.heg.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#002c46] hover:text-[#001a2a] underline font-semibold"
                  >
                    {APP_CONFIG.partners.heg.fullName} (HEG-Genève)
                  </a><br />
                  Campus de Battelle<br />
                  Rue de la Tambourine 17<br />
                  1227 Carouge, Suisse
                </p>
                <p className="text-gray-700">
                  <strong>Contact DPO :</strong> <a href="mailto:privacy@tutormatch-heg-ge.ch" className="text-[#002c46] hover:text-[#001a2a] underline">privacy@tutormatch-heg-ge.ch</a><br />
                  <strong>Réalisé par :</strong> <a 
                    href={APP_CONFIG.partners.aeheg.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#002c46] hover:text-[#001a2a] underline font-semibold"
                  >
                    {APP_CONFIG.partners.aeheg.name}
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Données collectées</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Données d'inscription</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Nom, prénom, email</li>
                <li>Filière d'études et année</li>
                <li>Numéro de téléphone (optionnel)</li>
                <li>Préférences de contact</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Données de profil tuteur</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Matières enseignées</li>
                <li>Tarifs et disponibilités</li>
                <li>Expérience et qualifications</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Données d'utilisation</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Logs de connexion et adresses IP</li>
                <li>Interactions sur la plateforme</li>
                <li>Évaluations sous forme d'étoiles (notes de 1 à 5)</li>
                <li>Historique des sessions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Finalités du traitement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous utilisons vos données personnelles pour les finalités suivantes :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Fourniture du service :</strong> Mise en relation, gestion des profils, communication</li>
                <li><strong>Sécurité :</strong> Prévention de la fraude, vérification d'identité</li>
                <li><strong>Amélioration :</strong> Analyse d'usage, développement de nouvelles fonctionnalités</li>
                <li><strong>Communication :</strong> Notifications importantes, support client</li>
                <li><strong>Obligations légales :</strong> Conformité réglementaire, comptabilité</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Base légale du traitement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Conformément à la LPD suisse et au RGPD, nos traitements reposent sur :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Consentement :</strong> Pour les communications marketing (optionnelles)</li>
                <li><strong>Exécution du contrat :</strong> Pour la fourniture des services de mise en relation</li>
                <li><strong>Intérêt légitime :</strong> Pour la sécurité et l'amélioration de la plateforme</li>
                <li><strong>Obligation légale :</strong> Pour la comptabilité et la conformité fiscale</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Partage des données</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Partage avec d'autres utilisateurs et utilisatrices</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Certaines informations de votre profil sont visibles par d'autres utilisateurs et utilisatrices pour faciliter la mise en relation :
                nom, filière, matières, disponibilités, notes sous forme d'étoiles.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Prestataires de services</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous partageons vos données avec des prestataires tiers uniquement dans la mesure nécessaire :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Hébergement des données (Supabase, AWS)</li>
                <li>Services d'email (Infomaniak)</li>
                <li>Analytics (Google Analytics - anonymisé)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Transferts internationaux</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Certains de nos prestataires peuvent être situés en dehors de la Suisse/UE. 
                Dans ce cas, nous nous assurons que des garanties appropriées sont en place 
                (clauses contractuelles types, décisions d'adéquation).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Conservation des données</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous conservons vos données personnelles pendant les durées suivantes :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Comptes actifs :</strong> Tant que votre compte est actif</li>
                <li><strong>Comptes supprimés :</strong> 30 jours après suppression (sauf obligations légales)</li>
                <li><strong>Logs de sécurité :</strong> 12 mois maximum</li>
                <li><strong>Évaluations :</strong> Conservées même après suppression du compte (anonymisées)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Vos droits</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Conformément à la LPD et au RGPD, vous disposez des droits suivants :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    <i className="fas fa-eye text-blue-600 mr-2"></i>
                    Droit d'accès
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Obtenez une copie de toutes vos données personnelles
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    <i className="fas fa-edit text-green-600 mr-2"></i>
                    Droit de rectification
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Corrigez les données inexactes ou incomplètes
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    <i className="fas fa-trash text-red-600 mr-2"></i>
                    Droit à l'effacement
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Demandez la suppression de vos données
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    <i className="fas fa-download text-purple-600 mr-2"></i>
                    Droit à la portabilité
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Récupérez vos données dans un format structuré
                  </p>
                </div>
              </div>
              
              <p className="text-gray-700 leading-relaxed">
                Pour exercer ces droits, contactez-nous à <strong>privacy@tutormatch-heg-ge.ch</strong> 
                avec une pièce d'identité pour vérification.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies et technologies similaires</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Nous utilisons des cookies et technologies similaires pour :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Maintenir votre session de connexion</li>
                <li>Mémoriser vos préférences</li>
                <li>Analyser l'utilisation de la plateforme (avec votre consentement)</li>
                <li>Améliorer la sécurité</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur 
                ou via notre bandeau de consentement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Réclamations</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Si vous estimez que vos droits en matière de protection des données ne sont pas respectés, 
                vous pouvez déposer une réclamation auprès de :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>Préposé fédéral à la protection des données et à la transparence (PFPDT)</strong><br />
                  Feldeggweg 1<br />
                  3003 Berne, Suisse<br />
                  <strong>Website :</strong> www.edoeb.admin.ch
                </p>
              </div>
            </section>

            <div className="border-t pt-8 mt-8">
              <p className="text-gray-600 text-center">
                Cette politique de confidentialité est conforme à la Loi fédérale sur la protection des données (LPD) suisse 
                et au Règlement général sur la protection des données (RGPD) européen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
